<template>
  <div class="table-wrap">
    <!-- Single sample type header at the top -->
    <div class="table-title-div" v-if="sampleData && sampleData.sample && sampleData.sample.sampleType">
          <p class="table-title">{{ sampleData.sample.sampleType  }}</p>
        </div>
    <!-- Grid layout for tables -->
    <div class="tables-grid">
      <div v-for="(tableData, tableIndex) in tablesData" :key="tableIndex" class="table-section">
       
        
        <!-- Skeleton loader while loading -->
        <div v-if="loading" class="skeleton-loader">
          <div class="skeleton-header"></div>
          <div v-for="i in 6" :key="i" class="skeleton-row">
            <div class="skeleton-cell"></div>
            <div class="skeleton-cell"></div>
            <div class="skeleton-cell"></div>
          </div>
        </div>
        
        <v-data-table 
          v-else-if="tableData.items.length > 0" 
          :headers="tableData.headers"
          :items="tableData.items"
          :loading="false" 
          fixed-header 
          max-height="calc(50vh - 200px)"
          class="sample-info-table">
          <template v-slot:headers>
            <tr class="main-header">
              <th class="sticky-header">{{ tableData.title }}</th>
              <th class="sticky-header">Value</th>
              <th class="sticky-header">Unit</th>
            </tr>
          </template>
          <template v-slot:body="{ items }">
            <tr v-for="(item, index) in items" :key="index">
              <td>{{ item.propertyName }}</td>
              <td>{{ item.value }}</td>
              <td>{{ item.unit }}</td>
            </tr>
          </template>
        </v-data-table>
        <div v-else class="no-data-message">
          <h4>No data available for {{ tableData.title }}.</h4>
        </div>
      </div>
    </div>
    
    <!-- Skeleton loader for initial loading state -->
    <div v-if="loading && tablesData.length === 0" class="skeleton-container">
      <div v-for="i in 4" :key="i" class="skeleton-table">
        <div class="skeleton-header"></div>
        <div v-for="j in 6" :key="j" class="skeleton-row">
          <div class="skeleton-cell"></div>
          <div class="skeleton-cell"></div>
          <div class="skeleton-cell"></div>
        </div>
      </div>
    </div>
    
    <div v-else-if="!loading && tablesData.length === 0" class="no-data-message">
      <h4>No fluids test summary available for this sample.</h4>
    </div>
  </div>
</template>

<script>
import api from "../../../../axiosInterceptor";
import { getDisplayName, getSortOrder } from '../../../../utils/cache';

export default {
  name: "FluidsTestTab",
  data: () => ({
    loading: true,
    tablesData: [],
    sampleData: null,
    documentDownload: require("../../../../assets/Images/csv.svg")
  }),
  methods: {
    async fetchSampleData() {
      try {
        const sampleId = this.$route.params.sampleId;
        
        if (sampleId) {
          const response = await api.get(`/public/api/samples/${sampleId}`);
          
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.sampleData = response.data.data;
            
            await this.processData();
            await this.processHeaderData();
          } else {
            console.error('Error fetching sample data:', response?.data?.message);
          }
        }
      } catch (error) {
        console.error('Error fetching sample data:', error);
      }
    },

    async processHeaderData() {
      try {
        const headerSortOrder = await getSortOrder("sample", "sample_fluid_tests_summary", "header");
        
        if (this.sampleData && this.sampleData.pageData && this.sampleData.pageData.fluidTestsSummaryHeader) {
          const headerData = this.sampleData.pageData.fluidTestsSummaryHeader;
          const headers = [];
          
          if (Array.isArray(headerData)) {
            const sortedHeader = [...headerData].sort((a, b) => {
              const indexA = headerSortOrder.indexOf(a.key);
              const indexB = headerSortOrder.indexOf(b.key);
              return indexA - indexB;
            });

            for (const item of sortedHeader) {
              const { key, value } = item;
              
              const camelCaseKey = key
                .split(' ')
                .map((word, index) => 
                  index === 0 
                    ? word.toLowerCase() 
                    : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join('');

              let displayName, digits;
              try {
                const res = await getDisplayName('sample', camelCaseKey);
                displayName = res.displayName || key;
                digits = res.digits != null ? res.digits : 0;
              } catch {
                displayName = key;
                digits = 0;
              }

              headers.push({
                displayName: displayName,
                key: camelCaseKey,
                value: value,
                digits: digits,
                sortOrder: headers.length + 1
              });
            }
          }

          this.$emit('data-updated', {
            headers: headers,
            testID: this.sampleData?.ID || ""
          });
        } else {
          const fallbackHeaders = [
            { displayName: "Sample ID", key: "sampleId", value: this.sampleData?.fluidSampleID || this.sampleData?.ID, digits: 0, sortOrder: 1 },
            { displayName: "Asset", key: "asset", value: this.sampleData?.location?.asset, digits: 0, sortOrder: 2 },
            { displayName: "Field", key: "field", value: this.sampleData?.location?.field, digits: 0, sortOrder: 3 },
            { displayName: "Well", key: "well", value: this.sampleData?.location?.well, digits: 0, sortOrder: 4 }
          ];

          this.$emit('data-updated', {
            headers: fallbackHeaders,
            testID: this.sampleData?.ID || ""
          });
        }
      } catch (error) {
        console.error('Error processing header data:', error);
        const fallbackHeaders = [
          { displayName: "Sample ID", key: "sampleId", value: this.sampleData?.fluidSampleID || this.sampleData?.ID, digits: 0, sortOrder: 1 },
          { displayName: "Asset", key: "asset", value: this.sampleData?.location?.asset, digits: 0, sortOrder: 2 },
          { displayName: "Field", key: "field", value: this.sampleData?.location?.field, digits: 0, sortOrder: 3 },
          { displayName: "Well", key: "well", value: this.sampleData?.location?.well, digits: 0, sortOrder: 4 }
        ];

        this.$emit('data-updated', {
          headers: fallbackHeaders,
          testID: this.sampleData?.ID || ""
        });
      }
    },

    getTableSortOrderParam(sampleType) {
      switch (sampleType) {
        case "Bottomhole Sample (BHS)":
          return "table_bhs";
        case "Recombined Reservoir Fluid (RRF)":
          return "table_rrf";
        case "Separator Gas":
          return "table_sp_gas";
        case "Separator Liquid":
          return "table_sp_liquid";
        case "Separator Oil":
          return "table_sto_stc";
        case "Wellhead Sample (WHS)":
          return "table_whs";
        case "Oil Based Mud (OBM)":
        case "Water Based Mud (WBM)":
          return "table_obm_wbm";
        default:
          return "table_comingled";
      }
    },

    async processData() {
      if (!this.sampleData) {
        return;
      }

      try {
        const sampleType = this.sampleData.sample.sampleType;
        
        const tableSortOrderParam = this.getTableSortOrderParam(sampleType);
        
        const tablePositions = this.getTablePositionsForSampleType(sampleType);
        
        this.tablesData = [];
        
        for (const position of tablePositions) {
          try {
            const sortOrder = await getSortOrder("sample", "sample_fluid_tests_summary", position);
            if (sortOrder && sortOrder.length > 0) {
              const tableData = await this.createTableData(sortOrder, position, sampleType);
              if (tableData.items.length > 0) {
                this.tablesData.push(tableData);
              }
            }
          } catch (error) {
            console.warn(`No sort order found for position: ${position}`);
          }
        }

        this.loading = false;
      } catch (error) {
        console.error('Error processing fluids test data:', error);
        this.loading = false;
      }
    },

    getTablePositionsForSampleType(sampleType) {
      let positions;
      switch (sampleType) {
        case "Bottomhole Sample (BHS)":
          positions = [
            "table_bhs_reservoir_summary",
            "table_bhs_fluid_properties_at _reservoir_conditions",
            "table_bhs_fluid_properties_at_saturation_conditions",
            "table_bhs_flashed_gas_properties",
            "table_bhs_flashed_liquid_properties",
            "table_bhs_fobm_contamination"
          ];
          break;
        case "Recombined Reservoir Fluid (RRF)":
          positions = [
            "table_rrf_reservoir_summary",
            "table_rrf_fluid_properties_at _reservoir_conditions",
            "table_rrf_fluid_properties_at_saturation_conditions",
            "table_rrf_flashed_gas_properties",
            "table_rrf_flashed_liquid_properties"
          ];
          break;
        case "Separator Gas":
          positions = [
            "table_sp_gas_wellhead_gas_reservoir_summary",
            "table_sp_gas_wellhead_gas_gas_properties",
            "table_sp_liquid_wellhead_liquid_flashed_gas_properties"
          ];
          break;
        case "Separator Liquid":
          positions = [
            "table_sp_liquid_wellhead_liquid_reservoir_summary",
            "table_sp_liquid_wellhead_liquid_flashed_liquid_properties"
          ];
          break;
        case "Separator Oil":
          positions = [
            "table_sto_stc_reservoir_summary",
            "table_sto_stc_oil_properties"
          ];
          break;
        case "Wellhead Sample (WHS)":
          positions = [
            "table_whs_reservoir_summary",
            "table_whs_fluid_properties"
          ];
          break;
        case "Oil Based Mud (OBM)":
        case "Water Based Mud (WBM)":
          positions = [
            "table_obm_wbm_mud_properties"
          ];
          break;
        default:
          positions = ["table_comingled"];
          break;
      }
      
      return positions;
    },

    groupSortOrderByTables(sortOrder) {
      return [sortOrder];
    },

    findKeyInDataStructure(data, targetKey, path = '') {
      const results = [];
      
      if (typeof data === 'object' && data !== null) {
        if (targetKey in data) {
          results.push({
            value: data[targetKey],
            path: path ? `${path}.${targetKey}` : targetKey,
            context: data
          });
        }
        
        for (const [key, value] of Object.entries(data)) {
          const newPath = path ? `${path}.${key}` : key;
          
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const arrayPath = `${newPath}[${i}]`;
              const arrayResults = this.findKeyInDataStructure(value[i], targetKey, arrayPath);
              results.push(...arrayResults);
            }
          } else if (typeof value === 'object' && value !== null) {
            const nestedResults = this.findKeyInDataStructure(value, targetKey, newPath);
            results.push(...nestedResults);
          }
        }
      }
      
      return results;
    },

    async createTableData(sortOrder, position, sampleType) {
      const dataSource = this.sampleData.sample;
      
      const headers = [
        {
          title: 'Property',
          key: 'propertyName',
          align: 'start',
          sortable: false
        },
        {
          title: 'Value',
          key: 'value',
          align: 'start',
          sortable: false
        },
        {
          title: 'Unit',
          key: 'unit',
          align: 'start',
          sortable: false
        }
      ];

      const items = [];
      const processedProperties = new Set(); // Track processed properties to avoid duplicates
      
      for (const key of sortOrder) {
        const searchResults = this.findKeyInDataStructure(dataSource, key);
        
        if (searchResults.length > 0) {
          searchResults.sort((a, b) => {
            const depthA = (a.path.match(/\./g) || []).length + (a.path.match(/\[/g) || []).length;
            const depthB = (b.path.match(/\./g) || []).length + (b.path.match(/\[/g) || []).length;
            return depthA - depthB; // Parent level (depth 0) comes first
          });
          
          for (let i = 0; i < searchResults.length; i++) {
            const result = searchResults[i];
            const value = result.value;
            const path = result.path;
            const context = result.context;
            
            if (processedProperties.has(key)) {
              continue; // Skip this instance if already processed
            }
            
            if (Array.isArray(value)) {
              for (let j = 0; j < value.length; j++) {
                const arrayItem = value[j];
                
                if (typeof arrayItem === 'object' && arrayItem !== null) {
                  for (const [itemKey, itemValue] of Object.entries(arrayItem)) {
                    if (processedProperties.has(itemKey)) {
                      continue;
                    }
                    
                    let displayName, digits;
                    try {
                      const res = await getDisplayName('sample', itemKey);
                      displayName = res.displayName || this.formatHeaderName(itemKey);
                      digits = res.digits != null ? res.digits : 0;
                    } catch (error) {
                      displayName = this.formatHeaderName(itemKey);
                      digits = 0;
                    }

                    const unit = this.getUnitForProperty(itemKey, arrayItem);
                    const formattedValue = this.formatValue(itemValue, digits);
                    
                    items.push({
                      propertyName: `${this.formatHeaderName(key)} - ${displayName}`,
                      value: formattedValue,
                      unit: unit,
                      originalKey: `${path}[${j}].${itemKey}`
                    });
                    
                    processedProperties.add(itemKey); // Mark as processed
                  }
                } else {
                  let displayName, digits;
                  try {
                    const res = await getDisplayName('sample', key);
                    displayName = res.displayName || this.formatHeaderName(key);
                    digits = res.digits != null ? res.digits : 0;
                  } catch (error) {
                    displayName = this.formatHeaderName(key);
                    digits = 0;
                  }

                  const unit = this.getUnitForProperty(key, context);
                  const formattedValue = this.formatValue(arrayItem, digits);
                  
                  items.push({
                    propertyName: `${displayName} [${j + 1}]`,
                    value: formattedValue,
                    unit: unit,
                    originalKey: `${path}[${j}]`
                  });
                }
              }
              
              processedProperties.add(key); // Mark array property as processed
            } else {
              let displayName, digits;
              try {
                const res = await getDisplayName('sample', key);
                displayName = res.displayName || this.formatHeaderName(key);
                digits = res.digits != null ? res.digits : 0;
              } catch (error) {
                displayName = this.formatHeaderName(key);
                digits = 0;
              }

              const unit = this.getUnitForProperty(key, context);
              const formattedValue = this.formatValue(value, digits);
              
              items.push({
                propertyName: displayName,
                value: formattedValue,
                unit: unit,
                originalKey: path
              });
              
              processedProperties.add(key); // Mark as processed
            }
          }
        }
      }

      const tableTitle = this.formatTableTitle(position, sampleType);

      return {
        title: tableTitle,
        headers: headers,
        items: items
      };
    },

    formatTableTitle(position, sampleType) {
      const titleMap = {
        'table_bhs_reservoir_summary': 'Reservoir Summary',
        'table_bhs_fluid_properties_at _reservoir_conditions': 'Fluid Properties at  Reservoir Conditions',
        'table_bhs_fluid_properties_at_saturation_conditions': 'Fluid Properties at  Saturation Conditions',
        'table_bhs_flashed_gas_properties': 'Flashed Gas Properties',
        'table_bhs_flashed_liquid_properties': 'Flashed Liquid Properties',
        'table_bhs_fobm_contamination': 'OBM Contamination',
        'table_rrf_reservoir_summary': 'Reservoir Summary',
        'table_rrf_fluid_properties_at _reservoir_conditions': 'Fluid Properties at Reservoir Conditions',
        'table_rrf_fluid_properties_at_saturation_conditions': 'Fluid Properties at Saturation Conditions',
        'table_rrf_flashed_gas_properties': 'Flashed Gas Properties',
        'table_rrf_flashed_liquid_properties': 'Flashed Liquid Properties',
        'table_sp_gas_wellhead_gas_reservoir_summary': 'Summary',
        'table_sp_gas_wellhead_gas_gas_properties': 'Flashed Gas Properties',
        'table_sp_liquid_wellhead_liquid_reservoir_summary': 'Summary',
        'table_sp_liquid_wellhead_liquid_liquid_properties': 'Flashed Liquid Properties',
        'table_sto_stc_reservoir_summary': 'Reservoir Summary',
        'table_sto_stc_oil_properties': 'Separator Oil - Oil Properties',
        'table_whs_reservoir_summary': 'Reservoir Summary',
        'table_whs_fluid_properties': 'WHS - Fluid Properties',
        'table_obm_wbm_mud_properties': 'Mud Properties',
        'table_comingled': 'Comingled Properties'
      };

      return titleMap[position] || `${sampleType} - ${position.replace('table_', '').replace(/_/g, ' ')}`;
    },

    formatValue(value, digits) {
      if (typeof value === 'number') {
        return digits > 0 ? value.toFixed(digits) : value;
      }
      return value;
    },

    getUnitForProperty(key, dataSource) {
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      return "";
    },

    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    downloadCSV() {
      if (this.tablesData && this.tablesData.length > 0) {
        const fileName = `fluids_test_summary_all_tables.csv`;
        
        let csvContent = '';
        
        this.tablesData.forEach((tableData, tableIndex) => {
          if (tableData.items && tableData.items.length > 0) {
            csvContent += `\n${tableData.title}\n`;
            csvContent += '='.repeat(tableData.title.length) + '\n';
            
            const csvHeader = tableData.headers.map((header) => header.title);
            csvContent += csvHeader.join(",") + "\n";
            
            const csvRows = tableData.items.map((item) => [
              item.propertyName,
              item.value,
              item.unit
            ]);
            csvContent += csvRows.map((row) => row.join(",")).join("\n");
            
            csvContent += "\n\n";
          }
        });
        
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error("Your browser does not support downloading files programmatically. Please try a different browser.");
        }
      } else {
        console.error("No data available in the fluids test summary tables to download.");
      }
    }
  },
  mounted() {
    this.fetchSampleData();
  }
};
</script>
